import { useLoaderData } from 'react-router-typesafe'
import { ButtonItem, ButtonOptions, Form as DXForm, GroupItem, SimpleItem } from 'devextreme-react/form'
import { useRef, useState } from 'react'
import { Column, DataGrid, DataGridRef, type DataGridTypes, Selection } from 'devextreme-react/data-grid'
import { Lookup } from 'devextreme-react'
import { LookupRef } from 'devextreme-react/cjs/lookup'
import { ArraySourceComposer } from '@/auth/api/connector'
import { ContactsUnifierLoader } from '@/routes/contacts/ContactsUnifier.route'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import notify from 'devextreme/ui/notify'
import { contatto_aziendale } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { ClickEvent } from 'devextreme/ui/button'
import { AxiosResponse } from 'axios'
import { useParams } from 'react-router-dom'
import DocumentHead from '@/components/document-head/DocumentHead'

interface SimilarityContact {
  id: number
  fullname: string
  ad_recipient: boolean
  nome: string
  cognome: string
  ruolo: string
  note: string
  titolo: string
  telefono: string
  attivo: boolean
  emails: [{ id: number; email: string }]
}
interface SimilarityResponse {
  sedi: [
    {
      id: number
      nome: string
      duplicated_contacts: [
        {
          similarity_group: SimilarityContact[]
        },
      ]
    },
  ]
}

interface UnifiesRequest {
  sites: {
    siteId: number
    duplicatedContacts: {
      chosenContact: SimilarityContact | undefined
      otherContacts: SimilarityContact[] | undefined
    }[]
  }[]
}

const ContactsUnifier = () => {
  const { title, clients } = useLoaderData<typeof ContactsUnifierLoader>()
  const { clientId } = useParams()

  const dataGridsRef = useRef<{
    [key: number]: DataGridRef<SimilarityContact, any> | null
  }>({})
  const [similarityResponse, setSimilarityResponse] = useState<SimilarityResponse | null>(null)
  const refAziendaLookup = useRef<LookupRef>(null)
  const [loadPanelVisible, setLoadPanleVisible] = useState<boolean>(false)
  const { service, client } = useQsAdminApiManager()
  const [buttonComputeEnabled, setButtonComputeEnabled] = useState<boolean>(true)
  const [buttonUnifiesEnabled, setButtonUnifiesEnabled] = useState<boolean>(false)
  const request: UnifiesRequest = { sites: [] }

  return (
    <>
      <h2>{title}</h2>
      <div id="contacts-unifier-container" className={'dx-card responsive-paddings'}>
        <LoadingPanel visible={loadPanelVisible}></LoadingPanel>
        <DXForm id={'azienda_form'}>
          <GroupItem colCount={1} colSpan={1}>
            <SimpleItem>
              <Lookup
                ref={refAziendaLookup}
                defaultValue={clientId ? clients.data.value[0] : undefined}
                dataSource={ArraySourceComposer('id', clients.data.value)}
                placeholder="Azienda"
                displayExpr="nome"
                searchEnabled={true}
                dropDownCentered={true}
                dropDownOptions={{ showTitle: true, title: 'Azienda', hideOnOutsideClick: true }}
                readOnly={!buttonComputeEnabled || clientId !== undefined}
              />
            </SimpleItem>
          </GroupItem>
          <GroupItem cssClass="last-group">
            <GroupItem cssClass="buttons-group" colCount={2}>
              <ButtonItem name="btn-compute">
                <ButtonOptions
                  disabled={!buttonComputeEnabled}
                  text="calcola"
                  icon="pulldown"
                  useSubmitBehavior={false}
                  width={'150px'}
                  onClick={async (e: ClickEvent) => {
                    if (refAziendaLookup.current?.instance().option('value')) {
                      setLoadPanleVisible(true)
                      try {
                        const response = await service.sede().query((sedeBuilder, sede) => {
                          sedeBuilder.filter(
                            sede.azienda.props.id.eq(refAziendaLookup.current?.instance().option('value').id),
                          )
                          sedeBuilder.expanding('contatti_aziendali', (contattiBuilder, contatto) => {
                            contattiBuilder.expanding('emails', (emailsBuilder, email) => {
                              emailsBuilder.select('id', 'email')
                            })
                          })
                          sedeBuilder.select('id', 'nome', 'contatti_aziendali')
                        })
                        const similarityRequest = {
                          id: refAziendaLookup.current?.instance().option('value').id,
                          nome: refAziendaLookup.current?.instance().option('value').nome,
                          sedi: response.data.value,
                        }
                        const aiResponse = await client.post('/api/contattoAziendale/similarity', similarityRequest, {
                          headers: {
                            'Content-Type': 'application/json',
                          },
                        })
                        const similarityResult: SimilarityResponse = aiResponse.data
                        console.log('similarityResult', similarityResult)
                        setSimilarityResponse(similarityResult)
                        setButtonUnifiesEnabled(true)
                        setButtonComputeEnabled(false)
                      } catch (error) {
                        notify(
                          {
                            message: `Errore recupero contatti: ${error}`,
                            type: 'error',
                            displayTime: 2000,
                          },
                          {
                            position: 'bottom center',
                            direction: 'up-push',
                          },
                        )
                      } finally {
                        setLoadPanleVisible(false)
                      }
                    } else
                      notify(
                        {
                          message: `Selezionare prima un' azienda.`,
                          type: 'warning',
                          displayTime: 2000,
                        },
                        {
                          position: 'bottom center',
                          direction: 'up-push',
                        },
                      )
                  }}
                />
              </ButtonItem>
              <ButtonItem name="btn-cancel">
                <ButtonOptions
                  disabled={!buttonUnifiesEnabled}
                  text="reset"
                  icon="revert"
                  useSubmitBehavior={false}
                  width={'150px'}
                  onClick={(e: ClickEvent) => {
                    setSimilarityResponse(null)
                    setButtonUnifiesEnabled(false)
                    setButtonComputeEnabled(true)
                  }}
                />
              </ButtonItem>
            </GroupItem>
          </GroupItem>
        </DXForm>
        {similarityResponse === null ? (
          <></>
        ) : (
          <>
            {similarityResponse.sedi.map(
              (
                sede: {
                  id: number
                  nome: string
                  duplicated_contacts: [
                    {
                      similarity_group: SimilarityContact[]
                    },
                  ]
                },
                indexSede: number,
              ) => {
                return (
                  <>
                    <h3>Sede: {sede.nome}</h3>
                    {sede.duplicated_contacts.length > 0 ? (
                      <>
                        {sede.duplicated_contacts.map(
                          (
                            duplicated_contact: {
                              similarity_group: SimilarityContact[]
                            },
                            indexDuplicated_contact: number,
                          ) => {
                            return (
                              <>
                                <h4>{duplicated_contact.similarity_group[0].fullname}</h4>
                                <DataGrid
                                  id={`sede_${indexSede}_duplicated_contacts_${indexDuplicated_contact}`}
                                  key={`sede_${indexSede}_duplicated_contacts_${indexDuplicated_contact}`}
                                  ref={(ref) => {
                                    dataGridsRef.current[
                                      Number(indexSede.toString() + indexDuplicated_contact.toString())
                                    ] = ref
                                  }}
                                  dataSource={duplicated_contact.similarity_group}
                                  noDataText="Nessuna contatto"
                                  className={'dx-card wide-card'}
                                  showBorders={false}
                                  showColumnLines={true}
                                  wordWrapEnabled={false}
                                  showRowLines={true}
                                  rowAlternationEnabled={true}
                                  allowColumnResizing={true}
                                  allowColumnReordering={true}
                                  width="100%"
                                  onContextMenuPreparing={(e: DataGridTypes.ContextMenuPreparingEvent<any, any>) => {
                                    if (e.row?.rowType === 'data' && e.component.getDataSource().items().length > 1) {
                                      const row: SimilarityContact = e.row.data
                                      if (!e.items) e.items = []
                                      e.items.push({
                                        text: 'Scorpora',
                                        icon: 'group',
                                        visible: true,
                                        onItemClick: () => {
                                          const newDuplicatedContactStarterGroup = {
                                            similarity_group: similarityResponse.sedi[indexSede].duplicated_contacts[
                                              indexDuplicated_contact
                                            ].similarity_group.filter((dup) => dup.id !== row.id),
                                          }
                                          similarityResponse.sedi[indexSede].duplicated_contacts[
                                            indexDuplicated_contact
                                          ] = newDuplicatedContactStarterGroup
                                          const newDuplicatedContacts =
                                            similarityResponse.sedi[indexSede].duplicated_contacts
                                          newDuplicatedContacts.push({ similarity_group: [row] })
                                          similarityResponse.sedi[indexSede].duplicated_contacts = newDuplicatedContacts
                                          setSimilarityResponse({
                                            sedi: similarityResponse.sedi,
                                          })
                                        },
                                      })
                                    }
                                  }}
                                >
                                  <Selection allowSelectAll={false} mode="multiple" />
                                  <Column dataField="id" caption="ID" />
                                  <Column
                                    dataField="fullname"
                                    caption="NOME"
                                    minWidth={100}
                                    cellRender={(cell: DataGridTypes.ColumnCellTemplateData) => (
                                      <div className="name-template">
                                        <div>{cell.data.fullname}</div>
                                        <div className="dx-field-item-label-text position">{cell.data.titolo}</div>
                                      </div>
                                    )}
                                  />
                                  <Column dataField="telefono" caption="TELEFONO" />
                                  <Column dataField="ad_recipient" dataType="boolean" caption="RIF ADSK" />
                                  <Column
                                    name={'emails'}
                                    caption={'EMAIL'}
                                    calculateCellValue={(rowData: contatto_aziendale) =>
                                      rowData.emails?.[0]?.email?.trim()
                                    }
                                  />
                                </DataGrid>
                              </>
                            )
                          },
                        )}
                      </>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%',
                          width: '100%',
                        }}
                      >
                        Nessun duplicato trovato
                      </div>
                    )}
                  </>
                )
              },
            )}
            <DXForm id={'unifies_form'}>
              <GroupItem cssClass="last-group">
                <GroupItem cssClass="buttons-group" colCount={1}>
                  <ButtonItem name="btn-unifies">
                    <ButtonOptions
                      disabled={!buttonUnifiesEnabled}
                      text="unifica"
                      icon="user"
                      useSubmitBehavior={false}
                      width={'150px'}
                      onClick={async (e: ClickEvent) => {
                        if (similarityResponse) {
                          for (let indexSede = 0; indexSede < similarityResponse.sedi.length; indexSede++) {
                            const site: {
                              siteId: number
                              duplicatedContacts: {
                                chosenContact: SimilarityContact | undefined
                                otherContacts: SimilarityContact[] | undefined
                              }[]
                            } = { siteId: similarityResponse.sedi[indexSede].id, duplicatedContacts: [] }
                            for (
                              let indexDuplicatedContact = 0;
                              indexDuplicatedContact < similarityResponse.sedi[indexSede].duplicated_contacts.length;
                              indexDuplicatedContact++
                            ) {
                              const group = dataGridsRef.current[
                                Number(indexSede.toString() + indexDuplicatedContact.toString())
                              ]
                                ?.instance()
                                .getSelectedRowsData()
                              if (group?.length && group.length > 1) {
                                notify(
                                  `Selezionare un contatto per gruppo di contatti. Verifica ${similarityResponse.sedi[indexSede].duplicated_contacts[indexDuplicatedContact].similarity_group[0].fullname}`,
                                  'warning',
                                  2000,
                                )
                                return
                              }
                              if (group?.length === 1) {
                                site.duplicatedContacts?.push({
                                  chosenContact: dataGridsRef.current[
                                    Number(indexSede.toString() + indexDuplicatedContact.toString())
                                  ]
                                    ?.instance()
                                    .getSelectedRowsData()[0],
                                  otherContacts: dataGridsRef.current[
                                    Number(indexSede.toString() + indexDuplicatedContact.toString())
                                  ]
                                    ?.instance()
                                    .getDataSource()
                                    .items()
                                    .filter(
                                      (contact) =>
                                        contact.id !==
                                        dataGridsRef.current[
                                          Number(indexSede.toString() + indexDuplicatedContact.toString())
                                        ]
                                          ?.instance()
                                          .getSelectedRowsData()[0].id,
                                    ),
                                })
                              }
                            }
                            request.sites.push(site)
                          }
                        }
                        console.log('request', request)

                        setLoadPanleVisible(true)

                        try {
                          const response: AxiosResponse<any, any> = await client.put(
                            '/api/contattoaziendale/unifies',
                            {
                              request,
                            },
                            {
                              headers: {
                                'Content-Type': 'application/json',
                              },
                            },
                          )
                          console.log('response', response)
                          notify(`Unificazione contatti avvenuta con successo`, 'success', 2000)
                        } catch (error) {
                          notify(`Errore unificazione contatti. Errore:  ${error}`, 'error', 2000)
                        } finally {
                          setLoadPanleVisible(false)
                          setSimilarityResponse(null)
                          setButtonUnifiesEnabled(false)
                          setButtonComputeEnabled(true)
                        }
                      }}
                    />
                  </ButtonItem>
                </GroupItem>
              </GroupItem>
            </DXForm>
          </>
        )}
      </div>
    </>
  )
}

export default ContactsUnifier
